import { AppSidebar } from "@/components/ui/app-sidebar";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Separator } from "@/components/ui/separator";
import {
  SidebarInset,
  SidebarProvider,
  SidebarTrigger,
} from "@/components/ui/sidebar";
import { Link, Outlet, useMatches, useLocation } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import React, { useEffect, useState, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { useAuth } from "@/context/auth.context";
import { api } from "@/lib/api";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { getDefaultAvatar } from "@/utils/getDefaultAvatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Bell, Bolt, ChevronDown, LogOut, UserCogIcon, CheckCheck, ArrowLeftRight } from "lucide-react";
import useModalState from "@/hooks/useModalState";
import LogoutModal from "../modal/LogoutModal";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";

const languages = [
  { value: "s1", label: "Kinyanrwanda", flag: "🇷🇼" },
  { value: "s2", label: "English", flag: "🇬🇧" },
  { value: "s3", label: "French", flag: "🇲🇫" },
];

export default function DashboardLayout() {
  const matches = useMatches();
  const location = useLocation();
  const { user, portalMode, switchPortal, canSwitchPortal } = useAuth();
  const logoutModal = useModalState();
  const navigate = useNavigate();

  const queryClient = useQueryClient();
  const [notifOpen, setNotifOpen] = useState(false);
  const notifRef = useRef<HTMLDivElement>(null);

  const { data: unreadCount } = useQuery(
    ["notif-unread-count"],
    async () => {
      const { data } = await api.get("/notifications/my/unread-count");
      return data?.data?.unreadCount || 0;
    },
    { refetchInterval: 30000, enabled: !!user }
  );

  const { data: notifications } = useQuery(
    ["my-notifications"],
    async () => {
      const { data } = await api.get("/notifications/my");
      return Array.isArray(data?.data) ? data.data : [];
    },
    { enabled: !!user }
  );

  const markAllRead = useMutation(
    async () => {
      await api.patch("/notifications/my/read");
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["notif-unread-count"]);
        queryClient.invalidateQueries(["my-notifications"]);
      },
    }
  );

  const markRead = useMutation(
    async (id: string) => {
      await api.patch(`/notifications/my/${id}/read`);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["notif-unread-count"]);
        queryClient.invalidateQueries(["my-notifications"]);
      },
    }
  );

  const handleNotificationClick = (n: any) => {
    if (!n.isRead) {
      markRead.mutate(n.id);
    }
    if (n.actionUrl) {
      navigate(n.actionUrl);
    } else if (n.relatedEntityType === "Loan") {
      navigate("/member/loans");
    } else if (n.relatedEntityType === "MonthlySavingDeclaration" || n.relatedEntityType === "MemberPayment") {
      navigate("/member/contributions");
    } else if (n.relatedEntityType === "SupportTicket") {
      navigate("/member/support");
    } else if (n.relatedEntityType === "PenaltyRecord") {
      navigate("/member/penalties");
    }
    setNotifOpen(false);
  };

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (notifRef.current && !notifRef.current.contains(e.target as Node)) {
        setNotifOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const latestNotifs = (notifications || []).slice(0, 5);
  const unread = unreadCount || 0;

  const getRoleDisplayName = () => {
    const leadershipRole =
      user?.leadershipRole ||
      user?.role?.name ||
      (typeof user?.role === "string" ? user.role : null);

    if (
      leadershipRole &&
      ["Technical Support", "President", "Secretary", "Accountant"].includes(
        leadershipRole
      )
    ) {
      return leadershipRole;
    }

    if (user?.isAdmin) {
      return "Case d'Entraide";
    }

    return leadershipRole || "Member";
  };

  const getDisplayName = () => {
    const rawName = (user?.fullName || user?.name || "").trim();
    const fallback = user?.firstName || rawName || "Member User";
    const knownRoleSuffixes = [
      "Technical Support",
      "President",
      "Secretary",
      "Accountant",
      "Admin",
      "Member",
    ];

    for (const suffix of knownRoleSuffixes) {
      const regex = new RegExp(`\\s+${suffix}$`, "i");
      if (regex.test(rawName)) {
        const cleaned = rawName.replace(regex, "").trim();
        return cleaned || fallback;
      }
    }

    return fallback;
  };

  const get2LetterInitials = (name: string) => {
    const [firstName, lastName] = name.split(" ");
    return `${firstName?.charAt(0) || ""}${lastName?.charAt(0) || ""}`;
  };

  let crumbs = matches
    // @ts-ignore
    .filter((match) => Boolean(match.handle?.crumb))
    .map((match) => ({
      // @ts-ignore
      crumb: match.handle.crumb(match.data),
      pathname: "/dashboard",
    }));

  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset className="bg-slate-100">
        <header className="flex sticky top-0 z-50 bg-white h-14 justify-between border-b shrink-0 items-center gap-1 sm:gap-2 duration-150 transition-[width,height] ease-linear group-has-[[data-collapsible=icon]]/sidebar-wrapper:h-12">
          <div className="flex items-center gap-1 sm:gap-2 px-2 sm:px-4 min-w-0 overflow-hidden">
            <SidebarTrigger className="-ml-1" />
            <Separator orientation="vertical" className="mr-2 h-4" />
            <Breadcrumb>
              <BreadcrumbList>
                {crumbs.map((crumb, index) => {
                  const isLast = index === crumbs.length - 1;
                  return (
                    <React.Fragment key={crumb.pathname}>
                      <BreadcrumbItem>
                        {isLast ? (
                          <BreadcrumbPage>{crumb.crumb}</BreadcrumbPage>
                        ) : (
                          <Link to={crumb.pathname}>{crumb.crumb}</Link>
                        )}
                      </BreadcrumbItem>
                      {!isLast && <BreadcrumbSeparator />}
                    </React.Fragment>
                  );
                })}
              </BreadcrumbList>
            </Breadcrumb>
          </div>
          <div className="px-2 sm:px-5 flex items-center gap-1 sm:gap-3">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="group flex items-center gap-1.5 rounded-full p-1 pr-1.5 hover:bg-gray-100 transition-colors"
                  aria-label="Open profile menu"
                >
                  <Avatar className="h-8 w-8 rounded-full ring-1 ring-gray-200">
                    <AvatarImage src={user?.profileUrl || getDefaultAvatar(user?.email || user?.name)} alt={getDisplayName()} />
                    <AvatarFallback className="rounded-full bg-[#f2f4f7] text-primary border-primary !border uppercase text-[11px]">
                      {get2LetterInitials(getDisplayName())}
                    </AvatarFallback>
                  </Avatar>
                  <span className="h-6 w-6 rounded-full bg-[#eef1f5] text-[#050500]/70 flex items-center justify-center group-hover:bg-[#e3e7ed]">
                    <ChevronDown className="h-3.5 w-3.5 shrink-0" />
                  </span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-56 rounded-lg" align="end" sideOffset={6}>
                <DropdownMenuLabel className="p-0 font-normal">
                  <div className="flex items-center gap-2 px-2 py-1.5 text-left text-sm">
                    <Avatar className="h-8 w-8 rounded-lg">
                      <AvatarImage src={user?.profileUrl || getDefaultAvatar(user?.email || user?.name)} alt={getDisplayName()} />
                      <AvatarFallback className="rounded-lg text-primary border-primary !border uppercase">
                        {get2LetterInitials(getDisplayName())}
                      </AvatarFallback>
                    </Avatar>
                    <div className="grid flex-1 text-left leading-tight">
                      <span className="truncate font-semibold text-sm">
                        {getDisplayName()}
                      </span>
                      <span className="truncate text-xs text-[#050500]/70">
                        {getRoleDisplayName()}
                      </span>
                    </div>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuGroup>
                  {canSwitchPortal && portalMode === "admin" && (
                    <DropdownMenuItem onClick={() => { switchPortal("member"); navigate("/member/dashboard"); }}>
                      <ArrowLeftRight size={16} />
                      Switch to My Account
                    </DropdownMenuItem>
                  )}
                  {canSwitchPortal && portalMode === "member" && (
                    <DropdownMenuItem onClick={() => { switchPortal("admin"); navigate("/dashboard"); }}>
                      <ArrowLeftRight size={16} />
                      Back to Management
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem onClick={() => navigate("/settings/account-settings")}>
                    <UserCogIcon size={16} />
                    Account Settings
                  </DropdownMenuItem>
                  {(user?.isAdmin ||
                    ["Technical Support", "President", "Secretary", "Accountant"].includes(
                      user?.leadershipRole || user?.role?.name
                    )) && (
                    <DropdownMenuItem onClick={() => navigate("/settings/general-information")}>
                      <Bolt size={16} />
                      System Settings
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem onClick={() => navigate("/member/notifications")}>
                    <Bell size={16} />
                    Notifications
                  </DropdownMenuItem>
                </DropdownMenuGroup>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => logoutModal.open()}>
                  <LogOut size={16} />
                  Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Notification Bell */}
            <div className="relative" ref={notifRef}>
              <button
                type="button"
                onClick={() => setNotifOpen(!notifOpen)}
                className="relative p-2 rounded-full hover:bg-gray-100 transition-colors"
                aria-label="Notifications"
              >
                <Bell className="h-5 w-5 text-slate-600" />
                {unread > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 h-4 w-4 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">
                    {unread > 9 ? "9+" : unread}
                  </span>
                )}
              </button>
              {notifOpen && (
                <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-lg border border-slate-200 z-50 overflow-hidden">
                  <div className="p-3 border-b flex items-center justify-between">
                    <p className="text-sm font-semibold text-slate-900">Notifications</p>
                    {unread > 0 && (
                      <button
                        onClick={() => markAllRead.mutate()}
                        className="text-xs text-[#009A44] hover:underline flex items-center gap-1"
                      >
                        <CheckCheck className="h-3 w-3" />
                        Mark all read
                      </button>
                    )}
                  </div>
                  <div className="max-h-72 overflow-y-auto">
                    {latestNotifs.length > 0 ? (
                      latestNotifs.map((n: any) => (
                        <button
                          key={n.id}
                          type="button"
                          onClick={() => handleNotificationClick(n)}
                          className={`w-full text-left px-3 py-2.5 border-b last:border-0 hover:bg-slate-50 transition-colors cursor-pointer ${!n.isRead ? "bg-green-50/40" : ""}`}
                        >
                          <p className={`text-xs ${!n.isRead ? "font-semibold" : ""} text-slate-900`}>{n.title}</p>
                          <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{n.message}</p>
                          <p className="text-[10px] text-slate-400 mt-0.5">
                            {n.createdAt ? format(new Date(n.createdAt), "MMM dd, HH:mm") : ""}
                          </p>
                        </button>
                      ))
                    ) : (
                      <div className="p-6 text-center">
                        <Bell className="h-6 w-6 text-slate-300 mx-auto mb-2" />
                        <p className="text-sm text-slate-500">No new notifications</p>
                      </div>
                    )}
                  </div>
                  <Link
                    to="/member/notifications"
                    className="block p-3 text-center text-xs text-[#009A44] font-medium hover:bg-slate-50 border-t"
                    onClick={() => setNotifOpen(false)}
                  >
                    View all notifications
                  </Link>
                </div>
              )}
            </div>

            <div className="hidden sm:block">
              <Select defaultValue="s2">
              <SelectTrigger
                id="select-37"
                className="[&>span]:flex h-9 [&>span]:items-center [&>span]:gap-2 [&>span_svg]:shrink-0 [&>span_svg]:text-muted-foreground/80"
              >
                <SelectValue placeholder="Select framework" />
              </SelectTrigger>
              <SelectContent className="[&_*[role=option]>span>svg]:shrink-0 [&_*[role=option]>span>svg]:text-muted-foreground/80 [&_*[role=option]>span]:end-2 [&_*[role=option]>span]:start-auto [&_*[role=option]>span]:flex [&_*[role=option]>span]:items-center [&_*[role=option]>span]:gap-2 [&_*[role=option]]:pe-8 [&_*[role=option]]:ps-2">
                {languages.map((ln) => (
                    <SelectItem key={ln.value} value={ln.value}>
                      <span className="text-lg leading-none">{ln.flag}</span>{" "}
                      <span className="truncate">{ln.label}</span>
                    </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          </div>
        </header>
        <div className="flex flex-1 flex-col gap-4 pt-0 px-3 sm:px-4 lg:px-6 min-w-0 max-w-full overflow-x-hidden">
          <AnimatePresence mode="wait">
            <Outlet key={location.pathname} />
          </AnimatePresence>
        </div>
      </SidebarInset>
      <LogoutModal open={logoutModal.isOpen} onClose={logoutModal.close} />
    </SidebarProvider>
  );
}
